/**
 * The instrument. Every API call, every waterfall trace, every dollar the
 * server said it spent — this is how the backend gets validated from a
 * browser instead of from CloudWatch.
 *
 * Costs are only ever SERVER-REPORTED. The prototype's flat "+$0.04 per
 * generate" was a prototype shortcut; estimating client-side would let the
 * meter disagree with reality, which defeats the point of having a meter.
 */
import { createStore } from './store';

export type TraceStep = {
  tier?: string;
  result?: string;
  assetId?: string;
  distance?: number;
  detail?: string;
};

export type CallLog = {
  id: number;
  at: number;
  method: string;
  path: string;
  status: number;
  ms: number;
  /** Distinct tier values the server reported anywhere in the payload. */
  tiers: string[];
  /** find_reusable_asset traces, flattened out of wherever they appeared. */
  trace: TraceStep[];
  /** Sum of costUsd on objects the server marked tier="generated". */
  costUsd: number;
  speculative: boolean;
  note?: string;
  error?: string;
};

type DevState = {
  calls: CallLog[];
  balance: number | null;
  ledger: Record<string, unknown>[];
  /** Client-side spend guards, all visible and all switchable. */
  speculateGate: boolean;
  speculateDrafts: boolean;
  /** Workaround switch for the client-trusted mode= param on GET /videos. */
};

const MAX_CALLS = 60;
let nextId = 1;

export const devStore = createStore<DevState>({
  calls: [],
  balance: null,
  ledger: [],
  speculateGate: true,
  speculateDrafts: true,
});

export function logCall(entry: Omit<CallLog, 'id' | 'at'>): void {
  const call: CallLog = { ...entry, id: nextId++, at: Date.now() };
  devStore.set((s) => ({ calls: [call, ...s.calls].slice(0, MAX_CALLS) }));
}

export function setBalance(balance: number | null, ledger: Record<string, unknown>[]): void {
  devStore.set({ balance, ledger });
}

export function sessionCostUsd(): number {
  return devStore.get().calls.reduce((sum, c) => sum + c.costUsd, 0);
}

/**
 * Walk an arbitrary response for the three things worth instrumenting.
 * The backend does not emit tier/costUsd on every payload yet, so this reads
 * whatever is there rather than insisting on a shape — a zero here means "the
 * server said nothing", which is itself the finding.
 */
export function harvest(payload: unknown): { tiers: string[]; trace: TraceStep[]; costUsd: number } {
  const tiers = new Set<string>();
  const trace: TraceStep[] = [];
  let costUsd = 0;
  const seen = new Set<unknown>();

  const walk = (node: unknown): void => {
    if (node === null || typeof node !== 'object') return;
    if (seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      for (const child of node) walk(child);
      return;
    }

    const obj = node as Record<string, unknown>;

    if (typeof obj.tier === 'string') {
      tiers.add(obj.tier);
      // Only generated art costs anything; pool/catalog/device hits are free.
      if (obj.tier === 'generated') costUsd += toNumber(obj.costUsd);
    }
    if (Array.isArray(obj.trace)) {
      for (const step of obj.trace) {
        if (step && typeof step === 'object') trace.push(step as TraceStep);
      }
    }

    for (const value of Object.values(obj)) walk(value);
  };

  walk(payload);
  return { tiers: [...tiers], trace, costUsd };
}

function toNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const n = Number.parseFloat(value); // Decimal-as-string, per the item shape
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
